import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseRollbackPlan,
  RollbackStepStatus,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { ROLLBACK_STATUS_LABELS } from "./releaseVocabulary";

/**
 * Adding a rollback step, or recording that one was run (§39).
 *
 * <p>One drawer for both, because they are the same row at two moments — one written calmly in
 * advance, one filled in while it is happening.
 *
 * <p>A failed rollback step demands a note. That is the worst moment of any deployment, and it is
 * exactly the point at which somebody is deciding what to do next: the note is what the next
 * person to look reads.
 */
export function RollbackStepDrawer({
  release,
  plan,
  stepId,
  mode = "record",
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  plan: ReleaseRollbackPlan;
  stepId: string | null;
  /**
   * What is being done to the step.
   *
   * <p>Recording an outcome and editing the wording are separate acts on separate endpoints:
   * one stamps who ran it and when, and a form that could do both would be a way to record a
   * step nobody performed.
   */
  mode?: "record" | "edit";
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const step = plan.steps.find((entry) => entry.id === stepId) ?? null;
  const recording = step != null && mode === "record";

  const [title, setTitle] = useState("");
  const [instruction, setInstruction] = useState("");
  const [estimate, setEstimate] = useState("");
  const [status, setStatus] = useState<RollbackStepStatus>("COMPLETED");
  const [notes, setNotes] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTitle(step?.title ?? "");
    setInstruction(step?.instruction ?? "");
    setEstimate(step?.estimatedMinutes ? String(step.estimatedMinutes) : "");
    setStatus(step?.status === "NOT_STARTED" ? "COMPLETED" : step?.status ?? "COMPLETED");
    setNotes(step?.notes ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepId]);

  const needsNote = recording && status === "FAILED";
  const canSubmit = recording
    ? !needsNote || notes.trim() !== ""
    : title.trim() !== "";

  const submit = () =>
    void run(
      () =>
        recording
          ? api.put(`/api/v1/releases/${release.id}/rollback/steps/${step.id}/status`, {
              status,
              notes: notes.trim() === "" ? null : notes.trim(),
              lockVersion: step.lockVersion,
            })
          : step
            ? /* Editing the wording of a step that has not run yet. */
              api.patch(`/api/v1/releases/${release.id}/rollback/steps/${step.id}`, {
                title: title.trim(),
                instruction: instruction.trim() === "" ? null : instruction.trim(),
                ownerId: step.owner?.id ?? null,
                estimatedMinutes: estimate === "" ? null : Number(estimate),
                lockVersion: step.lockVersion,
              })
            : api.post(`/api/v1/releases/${release.id}/rollback/steps`, {
                title: title.trim(),
                instruction: instruction.trim() === "" ? null : instruction.trim(),
                ownerId: null,
                estimatedMinutes: estimate === "" ? null : Number(estimate),
                lockVersion: null,
              }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        recording
          ? "Record a rollback step"
          : step
            ? "Edit this rollback step"
            : "Add a rollback step"
      }
      subtitle={
        recording || step ? step?.title : "What somebody would do to put things back."
      }
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : recording ? "Record" : step ? "Save" : "Add step"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        {recording ? (
          <>
            <Field label="What happened" required>
              <Select
                value={status}
                onChange={(event) => setStatus(event.target.value as RollbackStepStatus)}
              >
                {Object.entries(ROLLBACK_STATUS_LABELS)
                  .filter(([value]) => value !== "NOT_STARTED")
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field
              label="Notes"
              required={needsNote}
              hint={
                needsNote
                  ? "A rollback step that failed is the worst moment of the night. Say what happened."
                  : "Optional, and kept with the record."
              }
            >
              <Textarea
                rows={4}
                autoFocus
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="What to do" required>
              <Input
                autoFocus
                value={title}
                placeholder="Restore backend image 2.0.35"
                onChange={(event) => setTitle(event.target.value)}
              />
            </Field>
            <Field label="How" hint="Enough that somebody else could follow it under pressure.">
              <Textarea
                rows={3}
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
              />
            </Field>
            <Field label="Expected minutes">
              <Input
                type="number"
                min={0}
                value={estimate}
                onChange={(event) => setEstimate(event.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
